import config from "../../config/environment";

/**
 * Token storage (#41).
 *
 * The long-lived refresh token now lives in an HttpOnly cookie set by the API,
 * so it is no longer kept in JavaScript-readable storage. The short-lived
 * access token is held in sessionStorage (per-tab, cleared when the tab
 * closes); a same-tab reload keeps it, and a new tab / cold start re-bootstraps
 * the session via the refresh cookie.
 *
 * The previous implementation XOR-"encrypted" both tokens in localStorage —
 * reversible obfuscation that implied a protection it did not provide. The XOR
 * is removed. A one-time migration transparently upgrades sessions created by
 * the old build (tokens in localStorage) so existing users are not logged out.
 */

interface StoredToken {
  value: string;
  expiry: number; // Timestamp (ms) when the token expires
}

const ACCESS_KEY = config.auth.tokenKey;
const REFRESH_KEY = config.auth.refreshTokenKey;
// Non-sensitive marker that a session exists, so a fresh tab (empty
// sessionStorage) knows to attempt a cookie-based refresh rather than treating
// the user as logged out. Contains no token material.
const SESSION_FLAG_KEY = "auth_session";

/** Decode a value written by the old XOR scheme, for one-time migration only. */
const legacyDecrypt = (value: string): string => {
  const bytes = new Uint8Array(
    value.split("").map((char) => char.charCodeAt(0) ^ 0x41),
  );
  return new TextDecoder().decode(bytes);
};

/** Read + remove a legacy (XOR/localStorage) token, if present. */
const takeLegacyToken = (key: string): StoredToken | null => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  localStorage.removeItem(key);
  try {
    return JSON.parse(legacyDecrypt(raw)) as StoredToken;
  } catch {
    return null;
  }
};

const readAccessItem = (): StoredToken | null => {
  const raw = sessionStorage.getItem(ACCESS_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as StoredToken;
    } catch {
      sessionStorage.removeItem(ACCESS_KEY);
      return null;
    }
  }

  // Migrate a session created by the old (localStorage + XOR) build.
  const legacy = takeLegacyToken(ACCESS_KEY);
  if (legacy) {
    sessionStorage.setItem(ACCESS_KEY, JSON.stringify(legacy));
    localStorage.setItem(SESSION_FLAG_KEY, "1");
    return legacy;
  }
  return null;
};

const writeAccessItem = (token: string, expiryDuration: number): void => {
  const item: StoredToken = {
    value: token,
    expiry: Date.now() + expiryDuration,
  };
  sessionStorage.setItem(ACCESS_KEY, JSON.stringify(item));
  localStorage.setItem(SESSION_FLAG_KEY, "1");
  // Drop any stale legacy copy.
  localStorage.removeItem(ACCESS_KEY);
};

export const tokenStorage = {
  // ----- Access token (sessionStorage, short-lived) -----
  getAuthToken: (): string | null => {
    const item = readAccessItem();
    if (!item) return null;
    if (Date.now() > item.expiry) {
      sessionStorage.removeItem(ACCESS_KEY);
      return null;
    }
    return item.value;
  },

  setAuthToken: (
    token: string,
    expiryDuration = config.auth.tokenExpiry,
  ): void => {
    writeAccessItem(token, expiryDuration);
  },

  removeAuthToken: (): void => {
    sessionStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(ACCESS_KEY);
  },

  // ----- Refresh token (HttpOnly cookie; JS storage is legacy-read-only) -----
  /**
   * Returns a refresh token only if one lingers from the pre-cookie build, so
   * it can be replayed once as a transition fallback. New sessions keep the
   * refresh token in the cookie, where JS cannot read it, and this returns null.
   */
  getRefreshToken: (): string | null => {
    const raw = localStorage.getItem(REFRESH_KEY);
    if (!raw) return null;
    try {
      const item = JSON.parse(legacyDecrypt(raw)) as StoredToken;
      if (Date.now() > item.expiry) {
        localStorage.removeItem(REFRESH_KEY);
        return null;
      }
      return item.value;
    } catch {
      localStorage.removeItem(REFRESH_KEY);
      return null;
    }
  },

  /** No-op: the refresh token is managed by the API as an HttpOnly cookie. */
  setRefreshToken: (_token: string, _expiryDuration?: number): void => {
    // Intentionally not stored in JS-readable storage (#41).
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem(REFRESH_KEY);
  },

  // ----- Session state -----
  /**
   * Whether a session may exist — drives whether a 401 should attempt a
   * cookie-based refresh. True if we hold an access token, the session marker,
   * or a legacy refresh token.
   */
  hasSession: (): boolean => {
    return (
      sessionStorage.getItem(ACCESS_KEY) !== null ||
      localStorage.getItem(SESSION_FLAG_KEY) !== null ||
      localStorage.getItem(REFRESH_KEY) !== null ||
      localStorage.getItem(ACCESS_KEY) !== null
    );
  },

  isAuthTokenExpiringSoon: (thresholdMs = 30000): boolean => {
    const remaining = tokenStorage.getAuthTokenRemainingTime();
    return remaining !== null && remaining < thresholdMs;
  },

  getAuthTokenRemainingTime: (): number | null => {
    const item = readAccessItem();
    if (!item) return null;
    const remaining = item.expiry - Date.now();
    return remaining > 0 ? remaining : null;
  },

  clearTokens: (): void => {
    tokenStorage.removeAuthToken();
    tokenStorage.removeRefreshToken();
    localStorage.removeItem(SESSION_FLAG_KEY);
  },

  getAuthHeader: (): Record<string, string> => {
    const token = tokenStorage.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  getTokenData: (): { token: string; expiresAt: Date } | null => {
    const item = readAccessItem();
    if (!item) return null;
    if (Date.now() > item.expiry) {
      sessionStorage.removeItem(ACCESS_KEY);
      return null;
    }
    return { token: item.value, expiresAt: new Date(item.expiry) };
  },
};
